import React from 'react';
import { Shield, Star, Users, Globe, Award, CheckCircle, Lock, Heart } from 'lucide-react';
import { T } from '@/components/T';

interface TrustSignalsProps {
  pageType?: 'home' | 'city' | 'therapist' | 'article';
  entityData?: any;
  showRatings?: boolean;
  showSecurity?: boolean;
  showAccreditations?: boolean;
  showStatistics?: boolean;
}

const TrustSignals: React.FC<TrustSignalsProps> = ({
  pageType = 'home',
  entityData,
  showRatings = true,
  showSecurity = true,
  showAccreditations = true,
  showStatistics = true
}) => {
  const trustStatistics = [
    {
      icon: <Users className="w-8 h-8" />,
      value: "50,000+",
      label: <T>Patients Served</T>,
      description: <T>Successful therapy connections worldwide</T>
    },
    {
      icon: <Globe className="w-8 h-8" />,
      value: "50+",
      label: <T>Countries</T>,
      description: <T>Global mental health coverage</T>
    },
    {
      icon: <Star className="w-8 h-8" />,
      value: "4.9/5",
      label: <T>Average Rating</T>,
      description: <T>Based on verified patient reviews</T>
    },
    {
      icon: <Shield className="w-8 h-8" />,
      value: "100%",
      label: <T>Verified Professionals</T>,
      description: <T>All therapists certified through our program</T>
    }
  ];

  const securityFeatures = [
    {
      icon: <Lock className="w-5 h-5" />,
      title: <T>End-to-End Encryption</T>,
      description: <T>All communications are encrypted with military-grade security</T>
    },
    {
      icon: <Shield className="w-5 h-5" />,
      title: <T>HIPAA Compliant</T>,
      description: <T>Full compliance with healthcare privacy regulations</T>
    },
    {
      icon: <CheckCircle className="w-5 h-5" />,
      title: <T>Identity Verification</T>,
      description: <T>Rigorous verification process for all mental health professionals</T>
    },
    {
      icon: <Heart className="w-5 h-5" />,
      title: <T>Crisis Support</T>,
      description: <T>24/7 emergency support and crisis intervention protocols</T>
    }
  ];

  const accreditations = [
    {
      name: <T>International Association of Therapists</T>,
      namePlain: "International Association of Therapists",
      description: <T>Certified member organization</T>,
      descriptionPlain: "Certified member organization",
      logo: "🏆"
    },
    {
      name: <T>World Mental Health Organization</T>,
      namePlain: "World Mental Health Organization",
      description: <T>Approved training provider</T>,
      descriptionPlain: "Approved training provider",
      logo: "🌍"
    },
    {
      name: <T>Spanish Society of Psychosomatic Medicine</T>,
      namePlain: "Spanish Society of Psychosomatic Medicine",
      description: <T>Presidential leadership</T>,
      descriptionPlain: "Presidential leadership",
      logo: "⚕️"
    },
    {
      name: <T>International Forum for Psychotherapy Training</T>,
      namePlain: "International Forum for Psychotherapy Training",
      description: <T>Founding organization</T>,
      descriptionPlain: "Founding organization",
      logo: "📚"
    }
  ];

  const patientReviews = [
    {
      rating: 5,
      text: <T>The platform helped me find an excellent therapist who specializes in my specific needs. The whole process was smooth and professional.</T>,
      textPlain: "The platform helped me find an excellent therapist who specializes in my specific needs. The whole process was smooth and professional.",
      author: <T>Sarah M.</T>,
      authorPlain: "Sarah M.",
      location: <T>New York, USA</T>,
      verified: true
    },
    {
      rating: 5,
      text: <T>After struggling to find quality mental health support locally, this platform connected me with a wonderful therapist who truly understands my situation.</T>,
      textPlain: "After struggling to find quality mental health support locally, this platform connected me with a wonderful therapist who truly understands my situation.",
      author: <T>Carlos R.</T>,
      authorPlain: "Carlos R.",
      location: <T>Madrid, Spain</T>,
      verified: true
    },
    {
      rating: 5,
      text: <T>The security and privacy measures give me complete confidence. My therapist is highly qualified and the sessions have been life-changing.</T>,
      textPlain: "The security and privacy measures give me complete confidence. My therapist is highly qualified and the sessions have been life-changing.",
      author: <T>Emma L.</T>,
      authorPlain: "Emma L.",
      location: <T>London, UK</T>,
      verified: true
    }
  ];

  return (
    <>
      {/* Trust Signals Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: (() => {
            try {
              const reviews = patientReviews.map(review => ({
                "@type": "Review",
                "reviewRating": {
                  "@type": "Rating",
                  "ratingValue": review.rating.toString(),
                  "bestRating": "5"
                },
                "reviewBody": review.textPlain,
                "author": {
                  "@type": "Person",
                  "name": review.authorPlain
                },
                "datePublished": new Date().toISOString().split('T')[0]
              }));
              const credentials = accreditations.map(acc => ({
                "@type": "EducationalOccupationalCredential",
                "name": acc.namePlain,
                "description": acc.descriptionPlain,
                "credentialCategory": "Professional Accreditation"
              }));
              return JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Organization",
                "name": "Global Therapy Connect",
                "url": "https://psychotherapist.help",
                "logo": "https://psychotherapist.help/logo.png",
                "aggregateRating": {
                  "@type": "AggregateRating",
                  "ratingValue": "4.9",
                  "reviewCount": "2847",
                  "bestRating": "5",
                  "worstRating": "1"
                },
                "review": reviews,
                "hasCredential": credentials
              });
            } catch {
              return JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Organization",
                "name": "Global Therapy Connect",
                "url": "https://psychotherapist.help"
              });
            }
          })()
        }}
      />

      <div className="bg-white py-16">
        <div className="container mx-auto px-4">
          {/* Trust Statistics */}
          {showStatistics && (
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                <T>Trusted by Thousands Worldwide</T>
              </h2>
              <p className="text-xl text-gray-600 mb-12 max-w-3xl mx-auto">
                <T>Our commitment to quality and safety has earned the trust of patients and professionals across the globe</T>
              </p>
              
              <div className="grid md:grid-cols-4 gap-8">
                {trustStatistics.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-blue-600 mb-4 flex justify-center">
                      {stat.icon}
                    </div>
                    <div className="text-4xl font-bold text-gray-900 mb-2">
                      {stat.value}
                    </div>
                    <div className="text-lg font-semibold text-gray-800 mb-2">
                      {stat.label}
                    </div>
                    <div className="text-sm text-gray-600">
                      {stat.description}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Security & Privacy */}
          {showSecurity && (
            <div className="bg-gray-50 rounded-lg p-8 mb-16">
              <div className="text-center mb-8">
                <Shield className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  <T>Your Security & Privacy Are Our Priority</T>
                </h3>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  <T>We implement the highest standards of security and privacy protection to ensure your mental health journey remains confidential and safe</T>
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {securityFeatures.map((feature, index) => (
                  <div key={index} className="bg-white rounded-lg p-6 text-center">
                    <div className="text-green-600 mb-4 flex justify-center">
                      {feature.icon}
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">
                      {feature.title}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {feature.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Professional Accreditations */}
          {showAccreditations && (
            <div className="mb-16">
              <div className="text-center mb-8">
                <Award className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  <T>Professional Accreditations & Memberships</T>
                </h3>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  <T>Our platform is recognized and accredited by leading mental health organizations worldwide</T>
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {accreditations.map((acc, index) => (
                  <div key={index} className="bg-white border rounded-lg p-6 text-center hover:shadow-md transition-shadow">
                    <div className="text-4xl mb-4">{acc.logo}</div>
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm">
                      {acc.name}
                    </h4>
                    <p className="text-xs text-gray-600">
                      {acc.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Patient Reviews & Testimonials */}
          {showRatings && (
            <div className="bg-blue-50 rounded-lg p-8">
              <div className="text-center mb-8">
                <div className="flex justify-center items-center gap-2 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-8 h-8 text-yellow-400 fill-current" />
                  ))}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  <T>4.9/5 Stars from Verified Patients</T>
                </h3>
                <p className="text-gray-600">
                  <T>Based on 2,847+ verified reviews from patients worldwide</T>
                </p>
              </div>
              
              <div className="grid md:grid-cols-3 gap-6">
                {patientReviews.map((review, index) => (
                  <div key={index} className="bg-white rounded-lg p-6">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex">
                        {[...Array(review.rating)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                        ))}
                      </div>
                      {review.verified && (
                        <div className="flex items-center gap-1 text-green-600">
                          <CheckCircle className="w-4 h-4" />
                          <span className="text-xs"><T>Verified</T></span>
                        </div>
                      )}
                    </div>
                    <p className="text-gray-700 mb-4 text-sm italic">
                      "{review.text}"
                    </p>
                    <div className="border-t pt-3">
                      <div className="font-semibold text-gray-900 text-sm">
                        {review.author}
                      </div>
                      <div className="text-gray-600 text-xs">
                        {review.location}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="text-center mt-8">
                <button className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors">
                  <T>Read All Reviews</T>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default TrustSignals;